/**
 * Auth Client
 * Frontend authentication utilities with CSRF protection
 */

export type User = {
  id: string;
  email: string;
  name?: string;
  createdAt: Date;
};

const CSRF_COOKIE_NAME = 'csrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

/**
 * Get CSRF token from cookie
 */
function getCSRFToken(): string | null {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    acc[key] = value;
    return acc;
  }, {} as Record<string, string>);

  return cookies[CSRF_COOKIE_NAME] || null;
}

/**
 * Fetch a CSRF token from the server
 * This sets the token in a cookie and returns it
 */
async function fetchCSRFToken(): Promise<string> {
  const response = await fetch('/api/auth/csrf', {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch CSRF token');
  }

  const data = await response.json() as { csrfToken: string };
  return data.csrfToken;
}

/**
 * Get or fetch CSRF token
 */
async function ensureCSRFToken(): Promise<string> {
  let token = getCSRFToken();
  if (!token) {
    token = await fetchCSRFToken();
  }
  return token;
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetch('/api/auth/me', {
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<User> {
  const csrfToken = await ensureCSRFToken();

  const response = await fetch('/api/auth/signin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      [CSRF_HEADER_NAME]: csrfToken,
    },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 401) {
      throw new Error('401 Unauthorized');
    } else if (status === 403) {
      throw new Error('403 Forbidden - CSRF validation failed');
    } else if (status === 429) {
      throw new Error('429 Too Many Requests');
    }
    throw new Error(`Sign in failed with status ${status}`);
  }

  return response.json();
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  const csrfToken = getCSRFToken();

  await fetch('/api/auth/signout', {
    method: 'POST',
    headers: csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {},
    credentials: 'include',
  });
}

/**
 * Sign up with email and password
 */
export async function signUp(
  email: string,
  password: string,
  name?: string
): Promise<User> {
  const csrfToken = await ensureCSRFToken();

  const response = await fetch('/api/auth/signup', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      [CSRF_HEADER_NAME]: csrfToken,
    },
    credentials: 'include',
    body: JSON.stringify({ email, password, name }),
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 403) {
      throw new Error('403 Forbidden - CSRF validation failed');
    }
    throw new Error(`Sign up failed with status ${status}`);
  }

  return response.json();
}
